import type { AttemptSummary } from '@/lib/api/assessments';

export interface AttemptHistoryProps {
  attempts: AttemptSummary[];
  maxAttempts?: number;
}

function pct(score: number | null, max: number | null): number | null {
  if (score === null || max === null || max <= 0) return null;
  return Math.round((score / max) * 100);
}

const STATUS_LABEL: Record<string, string> = {
  in_progress: 'In progress',
  submitted: 'Submitted',
  expired: 'Timed out',
};

/**
 * AttemptHistory lists the student's own attempts, newest first, with the best
 * score highlighted — the score that actually counts, since the backend mirrors
 * the best attempt into the gradebook.
 */
export function AttemptHistory({ attempts, maxAttempts }: AttemptHistoryProps) {
  if (attempts.length === 0) return null;

  const scored = attempts.filter((a) => a.score !== null);
  const bestPct = scored.length
    ? Math.max(...scored.map((a) => pct(a.score, a.max_score) ?? -1))
    : -1;
  // On a tied best score, badge the EARLIEST attempt (lowest attempt_no) — the
  // conventional "your personal best was set on attempt N" reading — rather
  // than whichever tied attempt happens to come first in API response order.
  const tiedForBest = scored.filter((a) => (pct(a.score, a.max_score) ?? -1) === bestPct);
  const bestID = tiedForBest.length
    ? tiedForBest.reduce((earliest, a) => (a.attempt_no < earliest.attempt_no ? a : earliest)).attempt_id
    : undefined;

  const ordered = [...attempts].sort((a, b) => b.attempt_no - a.attempt_no);

  return (
    <section className="mt-6">
      <h3 className="mb-3 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        Your attempts{maxAttempts ? ` (${attempts.length} of ${maxAttempts})` : ''}
      </h3>
      <ul className="space-y-2">
        {ordered.map((a) => {
          const p = pct(a.score, a.max_score);
          const isBest = a.attempt_id === bestID && p !== null;
          return (
            <li
              key={a.attempt_id}
              className={`flex flex-wrap items-center justify-between gap-x-4 gap-y-1 rounded-lg border px-4 py-3 text-sm ${
                isBest ? 'border-green-500/50 bg-green-500/5' : 'border-border bg-card'
              }`}
            >
              <div className="min-w-0">
                <span className="font-semibold">Attempt {a.attempt_no}</span>
                {isBest && (
                  <span className="ml-2 rounded-full bg-green-500/15 px-2 py-0.5 text-[11px] font-semibold text-green-600 dark:text-green-400">
                    Best
                  </span>
                )}
                <span className="ml-2 text-xs text-muted-foreground">
                  {STATUS_LABEL[a.status] ?? a.status}
                  {a.submitted_at ? ` · ${new Date(a.submitted_at).toLocaleString()}` : ''}
                </span>
              </div>
              <div className="shrink-0 tabular-nums">
                {p === null ? (
                  <span className="text-muted-foreground">—</span>
                ) : (
                  <>
                    <span className="font-semibold">
                      {a.score}/{a.max_score}
                    </span>
                    <span className="ml-2 text-xs text-muted-foreground">{p}%</span>
                  </>
                )}
              </div>
            </li>
          );
        })}
      </ul>
    </section>
  );
}
